import { useState, useEffect, useMemo, useCallback } from "react";
import { useTheme } from "../context/Themecontext";
import Api from "../api/Api";

const ITEMS_PER_PAGE = 8;

/* ============================================================
   Base de padrões fracos a bloquear (partilhada com o modal
   de alteração de senha)
   ============================================================ */
const WEAK_PATTERNS = [
  // Sequências numéricas
  "0000", "00000", "000000", "00000000",
  "1111", "11111", "111111", "11111111",
  "2222", "22222", "222222",
  "3333", "33333", "333333",
  "4444", "44444", "444444",
  "5555", "55555", "555555",
  "6666", "66666", "666666",
  "7777", "77777", "777777",
  "8888", "88888", "888888",
  "9999", "99999", "999999",

  "0123", "01234", "012345", "0123456", "01234567", "012345678", "0123456789",
  "1234", "12345", "123456", "1234567", "12345678", "123456789", "1234567890",
  "2345", "3456", "4567", "5678", "6789",
  "9876", "98765", "987654", "9876543", "98765432", "987654321", "9876543210",
  "8765", "7654", "6543", "5432", "4321", "3210",

  // Letras
  "aaaa", "aaaaa", "aaaaaa", "aaaaaaaa",
  "bbbb", "cccc", "dddd",
  "abcd", "abcde", "abcdef", "abcdefg", "abcdefgh",
  "qwerty", "qwertyui", "qwerty123",
  "asdf", "asdfg", "asdfgh",
  "zxcv", "zxcvb", "zxcvbn",
  "poiuy", "lkjhg", "mnbvc",

  // Palavras comuns
  "password", "password1", "password123", "password1234",
  "password2024", "password2025", "password2026",
  "pass", "passwd",
  "senha", "senha123", "senha2024", "senha2025", "senha2026",
  "admin", "admin123", "administrator",
  "root", "root123",
  "user", "guest", "login", "welcome", "welcome123",
  "default", "test", "teste", "demo", "letmein",
  "secret", "access", "master", "system",

  // Combinações muito usadas
  "abc123", "abc12345", "123abc", "pass123", "adminadmin",
  "qwerty123", "qwerty1", "iloveyou", "loveyou", "love123",
  "football", "soccer", "baseball", "dragon", "monkey",
  "shadow", "superman", "batman", "pokemon", "naruto",
  "goku", "internet",

  // Teclado
  "1q2w3e", "1qaz2wsx", "1qazxsw2", "qazwsx", "zaq12wsx",
  "qwe123", "qweasd", "asdf123", "zxc123",

  // Datas comuns
  "01011990", "01011991", "01011992", "01011993", "01011994",
  "01011995", "01011996", "01011997", "01011998", "01011999", "01012000",
  "2000", "2001", "2002", "2003", "2004", "2005",
  "2020", "2021", "2022", "2023", "2024", "2025", "2026",

  // Empresas/serviços
  "google", "facebook", "microsoft", "windows", "apple",
  "android", "gmail", "hotmail", "outlook",

  // Outros
  "changeme", "welcome1", "temp123", "temporary", "guest123",
  "admin2024", "admin2025", "admin2026", "rootroot", "trustno1",
  "freedom", "hello", "hello123", "mypassword", "mypassword123",
];

function hasSequential(value) {
  const lower = value.toLowerCase();
  return WEAK_PATTERNS.some((p) => lower.includes(p));
}

function evaluatePassword(pwd) {
  const checks = {
    length: pwd.length >= 8,
    uppercase: /[A-Z]/.test(pwd),
    lowercase: /[a-z]/.test(pwd),
    number: /[0-9]/.test(pwd),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
    noWeakPattern: pwd.length > 0 && !hasSequential(pwd),
  };

  let score = Object.values(checks).filter(Boolean).length;
  if (pwd.length >= 12) score += 1;

  let level = "none";
  if (pwd.length === 0) level = "none";
  else if (score <= 2) level = "weak";
  else if (score <= 4) level = "medium";
  else level = "strong";

  const isValid =
    checks.length && checks.uppercase && checks.lowercase &&
    checks.number && checks.special && checks.noWeakPattern;

  return { checks, score, level, isValid };
}

const LEVEL_CONFIG = {
  none:   { label: "",      bars: 0, color: "#e2eaf0" },
  weak:   { label: "Fraca", bars: 1, color: "#ef4444" },
  medium: { label: "Média", bars: 2, color: "#f59e0b" },
  strong: { label: "Forte", bars: 3, color: "#22c55e" },
};

function PasswordReq({ met, label }) {
  return (
    <div className={`us-req ${met ? "met" : ""}`}>
      <span className="us-req-icon">
        {met ? (
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
            <circle cx="12" cy="12" r="8" />
          </svg>
        )}
      </span>
      {label}
    </div>
  );
}

/* ============================================================
   UsersPage – Layout Profissional Centralizado com Paginação
   ============================================================ */
export default function UsersPage() {
  const { dark } = useTheme();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);

  const [perfis, setPerfis] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);

  const [modalMode, setModalMode] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [notification, setNotification] = useState(null);
  const [actionLoading, setActionLoading] = useState(false); // loading para ações como delete/save

  // ══════════ Tokens de cor – fundo realmente escuro no dark ══════════
  const t = dark
    ? {
        bg: "#080c14",
        bgCard: "#111827",
        bgHover: "rgba(0,144,231,0.08)",
        bgActive: "rgba(0,144,231,0.16)",
        bgInput: "#1a2232",
        bgDropdown: "#111827",
        border: "rgba(0,174,255,0.12)",
        borderActive: "rgba(0,174,255,0.35)",
        textPrimary: "#eef5ff",
        textSecondary: "#b0c7e0",
        textMuted: "#7b93b0",
        textActive: "#00b4ff",
        scrollbar: "rgba(0,174,255,0.2)",
        shadow: "0 12px 40px rgba(0,0,0,0.8)",
        rowHover: "rgba(0,144,231,0.06)",
        danger: "#ef4444",
        dangerBg: "rgba(239,68,68,0.12)",
        success: "#22c55e",
        successBg: "rgba(34,197,94,0.12)",
        overlay: "rgba(8,12,20,0.8)",
        glass: true,
      }
    : {
        bg: "#f4f7fc",
        bgCard: "#ffffff",
        bgHover: "#f0f6ff",
        bgActive: "#e6f0ff",
        bgInput: "#fafbfd",
        bgDropdown: "#ffffff",
        border: "#e2e8f0",
        borderActive: "rgba(0,100,230,0.4)",
        textPrimary: "#0f1a2e",
        textSecondary: "#475569",
        textMuted: "#8b9eb0",
        textActive: "#0057ff",
        scrollbar: "#cbd5e1",
        shadow: "0 8px 30px rgba(0,50,150,0.06)",
        rowHover: "#f8faff",
        danger: "#ef4444",
        dangerBg: "#fef2f2",
        success: "#16a34a",
        successBg: "#f0fdf4",
        overlay: "rgba(15,23,42,0.4)",
        glass: false,
      };

  // ── Carregar dados ─────────────────────────────────────────────
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await Api.getUser();
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setUsers(list);

      const perfisMap = new Map();
      const deptMap = new Map();
      list.forEach((u) => {
        if (u.id_pefil != null && u.perfil) perfisMap.set(u.id_pefil, u.perfil);
        if (u.id_departamento != null && u.departamento) deptMap.set(u.id_departamento, u.departamento);
      });
      setPerfis((prev) =>
        prev.length ? prev : Array.from(perfisMap, ([id, descricao]) => ({ id, descricao }))
      );
      setDepartamentos((prev) =>
        prev.length ? prev : Array.from(deptMap, ([id, descricao]) => ({ id, descricao }))
      );
    } catch {
      setErrorMsg("Não foi possível carregar os utilizadores.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try { if (typeof Api.getPerfil === "function") {
        const r = await Api.getPerfil();
        const list = Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : [];
        if (list.length) setPerfis(list);
      }} catch {}
      try { if (typeof Api.getDepartamento === "function") {
        const r = await Api.getDepartamento();
        const list = Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : [];
        if (list.length) setDepartamentos(list);
      }} catch {}
    })();
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!notification) return;
    const id = setTimeout(() => setNotification(null), 3500);
    return () => clearTimeout(id);
  }, [notification]);

  // Resetar página quando filtro muda
  useEffect(() => setPage(1), [search, statusFilter]);

  // ── Filtro e paginação ────────────────────────────────────────
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const q = search.toLowerCase();
      return (!search || u.nome?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)) &&
        (statusFilter === "all" || String(u.status) === statusFilter);
    });
  }, [users, search, statusFilter]);

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE) || 1;
  const safePage = Math.min(page, totalPages);
  const paginatedUsers = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filteredUsers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredUsers, safePage]);

  const activeCount = users.filter((u) => String(u.status) === "1").length;

  // ── Ações ────────────────────────────────────────────────────
  const openAdd = () => { setEditingUser(null); setModalMode("add"); };
  const openEdit = (u) => { setEditingUser(u); setModalMode("edit"); };
  const closeModal = () => { setModalMode(null); setEditingUser(null); };

  // Chamado pelo modal depois de guardar com sucesso:
  // fecha o modal, mostra a notificação e recarrega a tabela.
  const handleSaved = (msg) => {
    closeModal();
    setNotification({ type: "success", text: msg });
    loadUsers();
  };

  const handleDelete = async () => {
    if (!deletingUser) return;
    setActionLoading(true);
    try {
      await Api.deleteUser({id:deletingUser.id});
      setNotification({ type: "success", text: `"${deletingUser.nome}" eliminado.` });
      setDeletingUser(null);
      loadUsers();
    } catch {
      setNotification({ type: "error", text: "Erro ao eliminar." });
    } finally {
      setActionLoading(false);
    }
  };

  const initials = (name = "") =>
    name.split(" ").filter(Boolean).slice(0,2).map(w => w[0]?.toUpperCase()).join("");

  // ══════════════════════ Estilos CSS ══════════════════════
  const styles = `
    .users-page {
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
      background: ${t.bg};
      min-height: 100vh;
      padding: 32px 20px;
    }
    .users-container {
      max-width: 1200px;
      margin: 0 auto;
    }
    .us-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 24px;
      margin-bottom: 32px;
      flex-wrap: wrap;
    }
    .us-heading {
      font-size: 1.65rem;
      font-weight: 700;
      letter-spacing: -0.03em;
      color: ${t.textPrimary};
      margin-bottom: 4px;
    }
    .us-sub {
      font-size: 0.88rem;
      color: ${t.textMuted};
      font-weight: 400;
    }
    .us-btn-add {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      height: 44px;
      padding: 0 24px;
      border-radius: 14px;
      border: none;
      background: linear-gradient(135deg, #00aaff, #0044ee);
      color: #fff;
      font-weight: 600;
      font-size: 0.86rem;
      cursor: pointer;
      box-shadow: 0 6px 18px rgba(0,100,255,0.3);
      transition: all 0.25s;
      letter-spacing: -0.01em;
    }
    .us-btn-add:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 26px rgba(0,100,255,0.45);
    }
    .us-btn-add:active { transform: translateY(0); }

    .us-stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 18px;
      margin-bottom: 28px;
    }
    .us-stat-card {
      background: ${t.bgCard};
      border: 1px solid ${t.border};
      border-radius: 18px;
      padding: 22px 26px;
      display: flex;
      align-items: center;
      gap: 16px;
      transition: box-shadow 0.3s, border-color 0.3s;
    }
    .us-stat-card:hover {
      box-shadow: ${t.shadow};
      border-color: ${t.borderActive};
    }
    .us-stat-icon {
      width: 44px;
      height: 44px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: ${t.bgActive};
      color: ${t.textActive};
    }
    .us-stat-label {
      font-size: 0.74rem;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: ${t.textMuted};
      margin-bottom: 6px;
    }
    .us-stat-value {
      font-size: 1.8rem;
      font-weight: 700;
      letter-spacing: -0.04em;
      color: ${t.textPrimary};
      line-height: 1;
    }

    .us-toolbar {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 24px;
      flex-wrap: wrap;
    }
    .us-search-box {
      display: flex;
      align-items: center;
      gap: 8px;
      background: ${t.bgCard};
      border: 1px solid ${t.border};
      border-radius: 14px;
      padding: 0 16px;
      height: 44px;
      width: 300px;
      transition: border 0.2s, box-shadow 0.2s;
    }
    .us-search-box:focus-within {
      border-color: #0090e7;
      box-shadow: 0 0 0 3px rgba(0,144,231,0.18);
    }
    .us-search-box input {
      background: none; border: none; outline: none;
      font-size: 0.86rem; color: ${t.textPrimary}; width: 100%;
    }
    .us-search-box input::placeholder { color: ${t.textMuted}; }
    .us-search-icon { color: ${t.textMuted}; display: flex; flex-shrink: 0; }

    .us-filter-chip {
      height: 44px; padding: 0 20px; border-radius: 14px;
      border: 1px solid ${t.border};
      background: ${t.bgCard}; color: ${t.textSecondary};
      font-size: 0.84rem; font-weight: 600; cursor: pointer;
      transition: all 0.2s;
    }
    .us-filter-chip.active {
      background: ${t.bgActive}; color: ${t.textActive};
      border-color: ${t.borderActive};
      box-shadow: 0 0 0 2px rgba(0,144,231,0.18);
    }
    .us-filter-chip:hover:not(.active) {
      background: ${t.bgHover}; color: ${t.textPrimary};
    }

    .us-table-card {
      background: ${t.bgCard};
      border: 1px solid ${t.border};
      border-radius: 18px;
      overflow: hidden;
    }
    .us-table {
      width: 100%; border-collapse: collapse;
    }
    .us-table thead th {
      text-align: left; font-size: 0.7rem; font-weight: 700;
      letter-spacing: 0.08em; text-transform: uppercase;
      color: ${t.textMuted}; padding: 14px 22px;
      background: ${t.bgHover}; border-bottom: 1px solid ${t.border};
      white-space: nowrap;
    }
    .us-table tbody td {
      padding: 14px 22px; font-size: 0.88rem;
      color: ${t.textPrimary}; border-bottom: 1px solid ${t.border};
      vertical-align: middle;
    }
    .us-table tbody tr:last-child td { border-bottom: none; }
    .us-table tbody tr { transition: background 0.15s; }
    .us-table tbody tr:hover { background: ${t.rowHover}; }

    .us-user-cell { display: flex; align-items: center; gap: 12px; }
    .us-avatar {
      width: 38px; height: 38px; border-radius: 12px;
      background: linear-gradient(135deg, #00aaff, #0044ee);
      display: flex; align-items: center; justify-content: center;
      font-size: 0.78rem; font-weight: 700; color: #fff; flex-shrink: 0;
    }
    .us-user-name { font-weight: 600; color: ${t.textPrimary}; line-height: 1.2; }
    .us-user-email { font-size: 0.77rem; color: ${t.textMuted}; margin-top: 2px; }

    .us-badge {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 5px 12px; border-radius: 99px;
      font-size: 0.74rem; font-weight: 700; letter-spacing: 0.02em;
    }
    .us-badge.active   { background: ${t.successBg}; color: ${t.success}; }
    .us-badge.inactive { background: ${t.dangerBg}; color: ${t.danger}; }
    .us-badge-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }

    .us-role-badge {
      font-size: 0.77rem; font-weight: 600;
      color: ${t.textSecondary}; background: ${t.bgHover};
      padding: 5px 12px; border-radius: 8px; display: inline-block;
    }

    .us-row-actions { display: flex; align-items: center; gap: 6px; justify-content: flex-end; }
    .us-icon-btn {
      width: 36px; height: 36px; border-radius: 10px;
      border: 1px solid ${t.border}; background: transparent;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; color: ${t.textSecondary};
      transition: all 0.2s;
    }
    .us-icon-btn:hover { background: ${t.bgHover}; color: ${t.textActive}; border-color: ${t.borderActive}; }
    .us-icon-btn.danger:hover { background: ${t.dangerBg}; color: ${t.danger}; border-color: ${t.danger}; }

    .us-pagination {
      display: flex; align-items: center; justify-content: space-between;
      padding: 16px 22px; border-top: 1px solid ${t.border};
      color: ${t.textSecondary}; font-size: 0.84rem;
    }
    .us-pagination-controls { display: flex; align-items: center; gap: 4px; }
    .us-pagination-btn {
      width: 34px; height: 34px; border-radius: 8px;
      border: 1px solid ${t.border}; background: ${t.bgCard};
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; color: ${t.textSecondary}; font-weight: 500;
      transition: all 0.2s;
    }
    .us-pagination-btn:hover:not(:disabled) { background: ${t.bgHover}; color: ${t.textActive}; border-color: ${t.borderActive}; }
    .us-pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .us-pagination-num {
      width: 34px; height: 34px; border-radius: 8px; border: none;
      background: transparent; color: ${t.textSecondary}; cursor: pointer;
      font-weight: 500; transition: all 0.2s;
    }
    .us-pagination-num.active {
      background: ${t.bgActive}; color: ${t.textActive}; font-weight: 700;
    }
    .us-pagination-num:hover:not(.active) { background: ${t.bgHover}; color: ${t.textPrimary}; }

    /* Skeleton loading */
    .skeleton-row {
      display: flex; align-items: center; padding: 14px 22px;
      border-bottom: 1px solid ${t.border};
    }
    .skeleton-circle {
      width: 38px; height: 38px; border-radius: 12px;
      background: ${t.bgHover}; animation: pulse 1.6s infinite;
    }
    .skeleton-line {
      height: 12px; border-radius: 6px; background: ${t.bgHover};
      animation: pulse 1.6s infinite; margin-left: 12px;
    }
    .skeleton-line.short { width: 90px; }
    .skeleton-line.medium { width: 140px; }
    .skeleton-line.long { width: 200px; }
    @keyframes pulse {
      0% { opacity: 0.5; } 50% { opacity: 0.8; } 100% { opacity: 0.5; }
    }

    .us-error-banner {
      background: ${t.dangerBg}; color: ${t.danger};
      border: 1px solid rgba(239,68,68,0.25); border-radius: 14px;
      padding: 14px 20px; font-size: 0.85rem; margin-bottom: 24px;
      display: flex; align-items: center; gap: 10px;
    }

    /* Modal */
    .us-overlay {
      position: fixed; inset: 0; background: ${t.overlay};
      backdrop-filter: blur(6px); z-index: 800;
      display: flex; align-items: center; justify-content: center;
      padding: 20px; animation: usFadeIn 0.2s ease;
    }
    @keyframes usFadeIn { from { opacity: 0; } to { opacity: 1; } }
    .us-modal {
      background: ${t.bgDropdown}; border: 1px solid ${t.border};
      border-radius: 22px; box-shadow: ${t.shadow};
      width: 100%; max-width: 460px; max-height: 90vh; overflow-y: auto;
      animation: usModalIn 0.25s cubic-bezier(.22,1,.36,1);
    }
    @keyframes usModalIn {
      from { opacity: 0; transform: translateY(-12px) scale(0.96); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    .us-modal-header {
      padding: 24px 28px 20px; border-bottom: 1px solid ${t.border};
      display: flex; align-items: center; justify-content: space-between;
    }
    .us-modal-title { font-size: 1.15rem; font-weight: 700; color: ${t.textPrimary}; letter-spacing: -0.02em; }
    .us-modal-sub { font-size: 0.8rem; color: ${t.textMuted}; margin-top: 4px; }
    .us-modal-close {
      width: 32px; height: 32px; border-radius: 10px; border: none;
      background: ${t.bgHover}; color: ${t.textSecondary};
      display: flex; align-items: center; justify-content: center; cursor: pointer;
    }
    .us-modal-close:hover { background: ${t.bgActive}; color: ${t.textActive}; }
    .us-modal-body { padding: 22px 28px; display: flex; flex-direction: column; gap: 18px; }
    .us-field { display: flex; flex-direction: column; gap: 6px; }
    .us-field label { font-size: 0.78rem; font-weight: 600; color: ${t.textSecondary}; }
    .us-field input, .us-field select {
      height: 44px; border-radius: 12px; border: 1px solid ${t.border};
      background: ${t.bgInput}; color: ${t.textPrimary};
      padding: 0 16px; font-size: 0.86rem; outline: none;
      transition: border 0.2s, box-shadow 0.2s;
    }
    .us-field input:focus, .us-field select:focus {
      border-color: #0090e7; box-shadow: 0 0 0 3px rgba(0,144,231,0.15);
    }
    .us-field-hint { font-size: 0.74rem; color: ${t.textMuted}; }
    .us-field-error { font-size: 0.74rem; color: ${t.danger}; }
    .us-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

    /* Indicador de força de senha */
    .us-strength-wrap { margin-top: 10px; }
    .us-strength-row { display: flex; gap: 6px; margin-bottom: 6px; }
    .us-strength-bar { flex: 1; height: 5px; border-radius: 99px; background: ${t.border}; transition: background 0.3s ease; }
    .us-strength-meta { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
    .us-strength-label { font-size: 0.72rem; font-weight: 700; }
    .us-strength-hint { font-size: 0.7rem; color: ${t.textMuted}; }
    .us-requirements {
      display: grid; grid-template-columns: 1fr 1fr; gap: 7px 12px;
      background: ${t.bgHover}; border-radius: 12px;
      padding: 12px 14px; border: 1px solid ${t.border};
    }
    .us-req {
      display: flex; align-items: center; gap: 6px;
      font-size: 0.71rem; font-weight: 500; color: ${t.textMuted};
      transition: color 0.2s;
    }
    .us-req.met { color: ${t.success}; }
    .us-req-icon {
      width: 14px; height: 14px; border-radius: 50%;
      background: ${t.bgActive}; display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; transition: background 0.2s;
    }
    .us-req.met .us-req-icon { background: ${t.successBg}; color: ${t.success}; }
    .us-weak-warning {
      display: flex; align-items: flex-start; gap: 8px;
      font-size: 0.73rem; font-weight: 500; line-height: 1.5;
      color: ${dark ? "#fbbf24" : "#92400e"};
      padding: 10px 12px; margin-top: 10px;
      background: ${dark ? "rgba(245,158,11,0.12)" : "#fffbeb"};
      border-radius: 10px; border-left: 3px solid #f59e0b;
    }

    .us-toggle-row {
      display: flex; align-items: center; justify-content: space-between;
      padding: 14px 16px; border-radius: 12px; background: ${t.bgHover};
    }
    .us-toggle-row span { font-size: 0.84rem; font-weight: 600; color: ${t.textPrimary}; }
    .us-switch {
      width: 42px; height: 24px; border-radius: 99px; border: none;
      cursor: pointer; position: relative; padding: 0;
      transition: background 0.25s;
    }
    .us-switch::after {
      content: ''; position: absolute; width: 18px; height: 18px;
      border-radius: 50%; background: #fff; top: 3px;
      transition: left 0.22s; box-shadow: 0 1px 4px rgba(0,0,0,0.2);
    }
    .us-modal-footer {
      padding: 20px 28px 24px; display: flex; gap: 14px; justify-content: flex-end;
      border-top: 1px solid ${t.border};
    }
    .us-btn-secondary {
      height: 42px; padding: 0 22px; border-radius: 12px;
      border: 1px solid ${t.border}; background: transparent;
      color: ${t.textSecondary}; font-weight: 600; font-size: 0.84rem;
      cursor: pointer; transition: all 0.2s;
    }
    .us-btn-secondary:hover { background: ${t.bgHover}; color: ${t.textPrimary}; }
    .us-btn-primary {
      height: 42px; padding: 0 24px; border-radius: 12px; border: none;
      background: linear-gradient(135deg, #00aaff, #0044ee);
      color: #fff; font-weight: 600; font-size: 0.84rem; cursor: pointer;
      display: flex; align-items: center; gap: 8px;
      box-shadow: 0 4px 14px rgba(0,100,255,0.3);
      transition: all 0.2s;
    }
    .us-btn-primary:hover:not(:disabled) {
      box-shadow: 0 6px 18px rgba(0,100,255,0.45); transform: translateY(-1px);
    }
    .us-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
    .us-btn-danger {
      height: 42px; padding: 0 24px; border-radius: 12px; border: none;
      background: ${t.danger}; color: #fff; font-weight: 600; font-size: 0.84rem;
      cursor: pointer; transition: all 0.2s;
    }
    .us-btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }
    .mini-spinner {
      display: inline-block; width: 16px; height: 16px;
      border-radius: 50%; border: 2px solid rgba(255,255,255,0.3);
      border-top-color: #fff; animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    .us-delete-body {
      padding: 26px 28px; font-size: 0.9rem;
      color: ${t.textSecondary}; line-height: 1.6;
    }
    .us-delete-body strong { color: ${t.textPrimary}; }

    .us-toast {
      position: fixed; bottom: 28px; right: 28px; z-index: 900;
      display: flex; align-items: center; gap: 12px;
      padding: 16px 22px; border-radius: 16px;
      background: ${t.bgDropdown}; border: 1px solid ${t.border};
      box-shadow: ${t.shadow}; font-size: 0.86rem; font-weight: 500;
      color: ${t.textPrimary};
      animation: toastIn 0.3s ease;
    }
    @keyframes toastIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
    .us-toast-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }

    @media (max-width: 640px) {
      .us-table thead { display: none; }
      .us-table tbody tr {
        display: flex; flex-direction: column; gap: 8px;
        padding: 16px 18px;
      }
      .us-table tbody td { padding: 0; border-bottom: none; }
      .us-row-actions { justify-content: flex-start; margin-top: 6px; }
      .us-field-row { grid-template-columns: 1fr; }
      .us-requirements { grid-template-columns: 1fr; }
      .us-search-box { width: 100%; }
      .us-pagination { flex-direction: column; gap: 10px; }
    }
  `;

  return (
    <div className="users-page">
      <style>{styles}</style>
      <div className="users-container">
        {/* Header */}
        <div className="us-header">
          <div>
            <h1 className="us-heading">Utilizadores</h1>
            <p className="us-sub">Gerir contas, perfis e departamentos</p>
          </div>
          <button className="us-btn-add" onClick={openAdd}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Novo utilizador
          </button>
        </div>

        {errorMsg && (
          <div className="us-error-banner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {errorMsg}
          </div>
        )}

        {/* Toolbar */}
        <div className="us-toolbar">
          <div className="us-search-box">
            <span className="us-search-icon">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </span>
            <input placeholder="Pesquisar nome ou email..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {/* Table / Skeleton */}
        <div className="us-table-card">
          {loading ? (
            <div>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="skeleton-row">
                  <div className="skeleton-circle" />
                  <div className="skeleton-line medium" style={{ marginLeft: 12 }} />
                  <div style={{ flex: 1 }} />
                  <div className="skeleton-line short" />
                  <div style={{ width: 20 }} />
                  <div className="skeleton-line short" />
                </div>
              ))}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div style={{ padding: "70px 20px", textAlign: "center", color: t.textMuted }}>
              <div style={{ fontSize: "1rem", fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>Nenhum utilizador encontrado</div>
              <div style={{ fontSize: "0.85rem" }}>Ajuste os filtros ou crie um novo utilizador.</div>
            </div>
          ) : (
            <>
              <table className="us-table">
                <thead>
                  <tr>
                    <th>Utilizador</th>
                    <th>Perfil</th>
                    <th>Departamento</th>
                    <th style={{ textAlign: "right" }}>Acções</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map(u => (
                    <tr key={u.id}>
                      <td>
                        <div className="us-user-cell">
                          <div className="us-avatar">{initials(u.nome) || "?"}</div>
                          <div>
                            <div className="us-user-name">{u.nome}</div>
                            <div className="us-user-email">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className="us-role-badge">{u.perfil || "—"}</span></td>
                      <td>{u.departamento || "—"}</td>
                      <td>
                        <div className="us-row-actions">
                          <button className="us-icon-btn" title="Editar" onClick={() => openEdit(u)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
                          </button>
                          <button className="us-icon-btn danger" title="Eliminar" onClick={() => setDeletingUser(u)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Paginação */}
              <div className="us-pagination">
                <span>Mostrando {paginatedUsers.length} de {filteredUsers.length} utilizadores</span>
                <div className="us-pagination-controls">
                  <button className="us-pagination-btn" disabled={safePage <= 1} onClick={() => setPage(p => Math.max(1, p-1))}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i+1).map(num => (
                    <button key={num} className={`us-pagination-num ${num === safePage ? "active" : ""}`} onClick={() => setPage(num)}>
                      {num}
                    </button>
                  ))}
                  <button className="us-pagination-btn" disabled={safePage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p+1))}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {modalMode && (
        <UserFormModal
          mode={modalMode} user={editingUser}
          perfis={perfis} departamentos={departamentos}
          t={t} onClose={closeModal} onSaved={handleSaved}
          actionLoading={actionLoading} setActionLoading={setActionLoading}
        />
      )}
      {deletingUser && (
        <DeleteConfirmModal
          user={deletingUser} t={t}
          onCancel={() => setDeletingUser(null)}
          onConfirm={handleDelete}
          deleting={actionLoading}
        />
      )}

      {/* Toast */}
      {notification && (
        <div className="us-toast">
          <span className="us-toast-dot" style={{ background: notification.type==="success" ? t.success : t.danger }} />
          {notification.text}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════ MODAL FORMULÁRIO ════════════════════════ */
function UserFormModal({ mode, user, perfis, departamentos, t, onClose, onSaved, actionLoading, setActionLoading }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    nome: user?.nome ?? "",
    email: user?.email ?? "",
    password: "",
    id_pefil: user?.id_pefil ?? "",
    id_departamento: user?.id_departamento ?? "",
    status: user ? String(user.status) : "1",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // ── Força da senha ──────────────────────────────────────────
  const evaluation = useMemo(() => evaluatePassword(form.password), [form.password]);
  const levelInfo = LEVEL_CONFIG[evaluation.level];
  const showWeakWarning =
    form.password.length >= 8 &&
    evaluation.checks.uppercase &&
    evaluation.checks.lowercase &&
    evaluation.checks.number &&
    evaluation.checks.special &&
    !evaluation.checks.noWeakPattern;

  // Ao criar, a senha é obrigatória e tem de cumprir todos os critérios.
  // Ao editar, só bloqueia se o campo tiver algo escrito e não cumprir os critérios.
  const passwordOk = isEdit
    ? (form.password === "" || evaluation.isValid)
    : evaluation.isValid;

  const canSubmit = passwordOk;

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = "Nome obrigatório.";
    if (!form.email.trim()) e.email = "Email obrigatório.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Email inválido.";

    if (!isEdit) {
      if (!form.password) e.password = "Senha obrigatória.";
      else if (!evaluation.isValid) e.password = "A senha não cumpre os requisitos de segurança.";
    } else if (form.password && !evaluation.isValid) {
      e.password = "A senha não cumpre os requisitos de segurança.";
    }

    if (!form.id_pefil) e.id_pefil = "Selecciona um perfil.";
    if (!form.id_departamento) e.id_departamento = "Selecciona um departamento.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setActionLoading(true);
    setSubmitError("");
    try {
      if (isEdit) {
        const payload = { nome: form.nome, email: form.email, id_pefil: form.id_pefil, id_departamento: form.id_departamento, status: form.status,id:user.id };
        if (form.password) payload.password = form.password;
        await Api.updateUser(payload);
        onSaved(`"${form.nome}" atualizado.`);
      } else {
        await Api.addUser({ nome: form.nome, email: form.email, password: form.password, id_pefil: form.id_pefil, id_departamento: form.id_departamento });
        onSaved(`"${form.nome}" criado com sucesso.`);
      }
    } catch (err) {
      setSubmitError(err?.response?.data?.message || err?.message || "Erro ao guardar.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="us-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="us-modal">
        <div className="us-modal-header">
          <div>
            <div className="us-modal-title">{isEdit ? "Editar utilizador" : "Novo utilizador"}</div>
            <div className="us-modal-sub">{isEdit ? "Actualize os dados da conta." : "Crie uma nova conta de acesso."}</div>
          </div>
          <button className="us-modal-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="us-modal-body">
            {submitError && <div className="us-error-banner">{submitError}</div>}
            <div className="us-field">
              <label>Nome completo</label>
              <input value={form.nome} onChange={e => update("nome", e.target.value)} placeholder="Ex: Justino Benguela" />
              {errors.nome && <span className="us-field-error">{errors.nome}</span>}
            </div>
            <div className="us-field">
              <label>Email</label>
              <input type="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="nome@technext.ao" />
              {errors.email && <span className="us-field-error">{errors.email}</span>}
            </div>
            <div className="us-field">
              <label>{isEdit ? "Nova senha" : "Senha"}</label>
              <input type="password" value={form.password} onChange={e => update("password", e.target.value)} placeholder={isEdit ? "Deixe em branco para manter" : "Mínimo 8 caracteres"} />
              {errors.password && <span className="us-field-error">{errors.password}</span>}
              {isEdit && !errors.password && !form.password && <span className="us-field-hint">Só preencha para alterar a senha.</span>}

              {form.password.length > 0 && (
                <div className="us-strength-wrap">
                  <div className="us-strength-row">
                    {[0, 1, 2].map(i => (
                      <div
                        key={i}
                        className="us-strength-bar"
                        style={{ background: i < levelInfo.bars ? levelInfo.color : t.border }}
                      />
                    ))}
                  </div>
                  <div className="us-strength-meta">
                    <span className="us-strength-label" style={{ color: levelInfo.color }}>{levelInfo.label}</span>
                    <span className="us-strength-hint">
                      {evaluation.level === "weak" && "Adicione maiúsculas, números e símbolos"}
                      {evaluation.level === "medium" && "Quase lá! Adicione mais variedade"}
                      {evaluation.level === "strong" && "Excelente senha ✓"}
                    </span>
                  </div>
                  <div className="us-requirements">
                    <PasswordReq met={evaluation.checks.length} label="Mín. 8 caracteres" />
                    <PasswordReq met={evaluation.checks.uppercase} label="Letra maiúscula" />
                    <PasswordReq met={evaluation.checks.lowercase} label="Letra minúscula" />
                    <PasswordReq met={evaluation.checks.number} label="Número (0-9)" />
                    <PasswordReq met={evaluation.checks.special} label="Caractere especial" />
                    <PasswordReq met={evaluation.checks.noWeakPattern} label="Sem padrões óbvios" />
                  </div>
                  {showWeakWarning && (
                    <div className="us-weak-warning">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                      A senha contém sequências previsíveis (ex: "1234", "abcd", "qwerty"). Tente combinações aleatórias.
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="us-field-row">
              <div className="us-field">
                <label>Perfil</label>
                <select value={form.id_pefil} onChange={e => update("id_pefil", e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {perfis.map(p => <option key={p.id} value={p.id}>{p.descricao}</option>)}
                </select>
                {errors.id_pefil && <span className="us-field-error">{errors.id_pefil}</span>}
              </div>
              <div className="us-field">
                <label>Departamento</label>
                <select value={form.id_departamento} onChange={e => update("id_departamento", e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {departamentos.map(d => <option key={d.id} value={d.id}>{d.descricao}</option>)}
                </select>
                {errors.id_departamento && <span className="us-field-error">{errors.id_departamento}</span>}
              </div>
            </div>
          </div>
          <div className="us-modal-footer">
            <button type="button" className="us-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="us-btn-primary" disabled={actionLoading || !canSubmit}>
              {actionLoading ? <span className="mini-spinner" /> : null}
              {isEdit ? "Guardar alterações" : "Criar utilizador"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ════════════════════════ MODAL ELIMINAR ════════════════════════ */
function DeleteConfirmModal({ user, t, onCancel, onConfirm, deleting }) {
  return (
    <div className="us-overlay" onMouseDown={e => e.target === e.currentTarget && onCancel()}>
      <div className="us-modal" style={{ maxWidth: "420px" }}>
        <div className="us-modal-header">
          <div className="us-modal-title">Eliminar utilizador</div>
          <button className="us-modal-close" onClick={onCancel}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="us-delete-body">
          Tem a certeza que deseja eliminar <strong>{user.nome}</strong>? Esta ação é irreversível.
        </div>
        <div className="us-modal-footer">
          <button className="us-btn-secondary" onClick={onCancel}>Cancelar</button>
          <button className="us-btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      </div>
    </div>
  );
}